import { useState, useRef, useEffect, useCallback } from "react";
import { postIpc } from "../../lib/ipc";

interface ChatMessage {
  role: "user" | "agent";
  content: string;
  timestamp?: string;
}

interface ProviderInfo {
  id: string;
  display_name: string;
  active: boolean;
}

interface OllamaModel {
  name: string;
  parameter_size?: string;
}

interface ChatWorkbenchProps {
  projectName: string;
}

/* ── Provider icon SVGs (animated) ── */
const PROVIDER_ICONS: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  anthropic: {
    color: "#d4a27f",
    label: "Claude",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: "provider-pulse 2s ease-in-out infinite" }}>
        <path d="M13.5 2L6 14h4.5l-1 8L17 10h-4.5l1-8z" fill="currentColor" />
      </svg>
    ),
  },
  claude: {
    color: "#d4a27f",
    label: "Claude CLI",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: "provider-pulse 2s ease-in-out infinite" }}>
        <path d="M13.5 2L6 14h4.5l-1 8L17 10h-4.5l1-8z" fill="currentColor" />
      </svg>
    ),
  },
  ollama: {
    color: "#f8f8f2",
    label: "Ollama",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: "provider-bounce 1.5s ease infinite" }}>
        <circle cx="12" cy="8" r="5" stroke="currentColor" strokeWidth="2" />
        <path d="M5 20c0-3.87 3.13-7 7-7s7 3.13 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="10" cy="7" r="1" fill="currentColor" />
        <circle cx="14" cy="7" r="1" fill="currentColor" />
      </svg>
    ),
  },
  openai: {
    color: "#74aa9c",
    label: "OpenAI",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: "provider-spin 3s linear infinite" }}>
        <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 3a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm0 14a8 8 0 0 1-6.93-4 8 8 0 0 1 13.86 0A8 8 0 0 1 12 19z" fill="currentColor" />
      </svg>
    ),
  },
  codex: {
    color: "#50fa7b",
    label: "Codex",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: "provider-blink 1s step-end infinite" }}>
        <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
        <path d="M7 9l3 3-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="13" y1="15" x2="17" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
};

const DEFAULT_PROVIDER_ICON = {
  color: "#bd93f9",
  label: "AI",
  icon: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="2" />
      <path d="M2 17l10 5 10-5" stroke="currentColor" strokeWidth="2" />
      <path d="M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" />
    </svg>
  ),
};

/* ── Thinking dots animation ── */
function ThinkingDots() {
  return (
    <span className="chat-thinking-dots">
      <span />
      <span />
      <span />
    </span>
  );
}

/* ── Model selector dropdown ── */
function ModelSelector({
  providers,
  activeProvider,
  models,
  activeModel,
  onSelectProvider,
  onSelectModel,
}: {
  providers: ProviderInfo[];
  activeProvider: string;
  models: OllamaModel[];
  activeModel: string;
  onSelectProvider: (id: string) => void;
  onSelectModel: (name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const pIcon = PROVIDER_ICONS[activeProvider] || DEFAULT_PROVIDER_ICON;

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: "flex", alignItems: "center", gap: 6,
          background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 6, padding: "4px 10px", cursor: "pointer",
          color: pIcon.color, fontSize: 11, fontFamily: "'JetBrains Mono', monospace",
          transition: "all 0.15s",
        }}
        title="Select model"
      >
        <span style={{ display: "flex", alignItems: "center", color: pIcon.color }}>{pIcon.icon}</span>
        <span>{activeModel || pIcon.label}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 100,
          background: "#1e1e2e", border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 8, minWidth: 220, boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
          overflow: "hidden",
        }}>
          <div style={{ padding: "6px 10px", fontSize: 10, color: "#6272a4", textTransform: "uppercase", letterSpacing: 1 }}>
            Providers
          </div>
          {providers.map((p) => {
            const pi = PROVIDER_ICONS[p.id] || DEFAULT_PROVIDER_ICON;
            return (
              <button
                key={p.id}
                onClick={() => { onSelectProvider(p.id); if (p.id !== "ollama") setOpen(false); }}
                style={{
                  display: "flex", alignItems: "center", gap: 8, width: "100%",
                  padding: "8px 12px", border: "none", cursor: "pointer",
                  background: p.id === activeProvider ? "rgba(255,255,255,0.08)" : "transparent",
                  color: p.id === activeProvider ? pi.color : "#a8b2b8",
                  fontSize: 12, fontFamily: "'JetBrains Mono', monospace",
                  textAlign: "left", transition: "background 0.1s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.06)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = p.id === activeProvider ? "rgba(255,255,255,0.08)" : "transparent")}
              >
                <span style={{ display: "flex", color: pi.color }}>{pi.icon}</span>
                <span>{p.display_name}</span>
                {p.id === activeProvider && <span style={{ marginLeft: "auto", color: "#50fa7b", fontSize: 10 }}>active</span>}
              </button>
            );
          })}
          {activeProvider === "ollama" && models.length > 0 && (
            <>
              <div style={{ padding: "6px 10px", fontSize: 10, color: "#6272a4", textTransform: "uppercase", letterSpacing: 1, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                Ollama Models
              </div>
              {models.map((m) => (
                <button
                  key={m.name}
                  onClick={() => { onSelectModel(m.name); setOpen(false); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, width: "100%",
                    padding: "8px 12px 8px 20px", border: "none", cursor: "pointer",
                    background: m.name === activeModel ? "rgba(255,255,255,0.08)" : "transparent",
                    color: m.name === activeModel ? "#f8f8f2" : "#a8b2b8",
                    fontSize: 12, fontFamily: "'JetBrains Mono', monospace",
                    textAlign: "left", transition: "background 0.1s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.06)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = m.name === activeModel ? "rgba(255,255,255,0.08)" : "transparent")}
                >
                  <span style={{ fontSize: 10 }}>{m.parameter_size || ""}</span>
                  <span>{m.name}</span>
                  {m.name === activeModel && <span style={{ marginLeft: "auto", color: "#50fa7b", fontSize: 10 }}>selected</span>}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Suggestion chips for empty state ── */
const SUGGESTIONS = [
  "Explain the project structure",
  "Find potential bugs",
  "Summarize recent changes",
  "List all API endpoints",
];

export function ChatWorkbench({ projectName }: ChatWorkbenchProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [phase, setPhase] = useState("idle");
  const [streaming, setStreaming] = useState("");
  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [activeProvider, setActiveProvider] = useState("ollama");
  const [ollamaModels, setOllamaModels] = useState<OllamaModel[]>([]);
  const [activeModel, setActiveModel] = useState("");
  const messagesRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch providers + models on mount
  useEffect(() => {
    fetch("/api/providers").then(r => r.json()).then(data => {
      setProviders(data.providers || []);
      setActiveProvider(data.active || "ollama");
    }).catch(() => {});
    fetch("/api/ollama/status").then(r => r.json()).then(data => {
      setOllamaModels(data.models || []);
      setActiveModel(data.active_model || "");
    }).catch(() => {});
  }, []);

  const handleSelectProvider = (id: string) => {
    setActiveProvider(id);
    fetch("/api/provider", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ provider: id }),
    }).catch(() => {});
    // Refresh Ollama models if switching to Ollama
    if (id === "ollama") {
      fetch("/api/ollama/status").then(r => r.json()).then(data => {
        setOllamaModels(data.models || []);
        setActiveModel(data.active_model || "");
      }).catch(() => {});
    }
  };

  const handleSelectModel = (name: string) => {
    setActiveModel(name);
  };

  // Auto-scroll on new messages
  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Listen for SSE agent events
  useEffect(() => {
    const onText = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const text = typeof detail === "string" ? detail : detail?.text || detail?.data || String(detail || "");
      setStreaming((prev) => prev + text);
      setPhase("streaming");
    };

    const onDone = () => {
      setStreaming((prev) => {
        if (prev.trim()) {
          setMessages((msgs) => [...msgs, { role: "agent", content: prev.trim() }]);
        }
        return "";
      });
      setPhase("idle");
    };

    const onError = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const error = typeof detail === "string" ? detail : detail?.error || detail?.message || "An error occurred";
      setMessages((msgs) => [...msgs, { role: "agent", content: `Error: ${error}` }]);
      setStreaming("");
      setPhase("idle");
    };

    const onPhase = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setPhase(detail?.phase || detail || "idle");
    };

    window.addEventListener("opentool-agent-text", onText);
    window.addEventListener("opentool-agent-done", onDone);
    window.addEventListener("opentool-agent-error", onError);
    window.addEventListener("opentool-agent-phase", onPhase);

    return () => {
      window.removeEventListener("opentool-agent-text", onText);
      window.removeEventListener("opentool-agent-done", onDone);
      window.removeEventListener("opentool-agent-error", onError);
      window.removeEventListener("opentool-agent-phase", onPhase);
    };
  }, []);

  const submitMessage = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed || phase === "streaming" || phase === "pending") return;

    setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
    setInput("");
    setPhase("pending");

    // Send via web API if available, otherwise via IPC
    if (!window.ipc || (window.ipc as any).__web) {
      fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: trimmed,
          provider: activeProvider,
          ...(activeModel ? { model: activeModel } : {}),
        }),
      })
        .then(r => r.json())
        .then(data => {
          if (data.error) {
            setMessages(prev => [...prev, { role: 'agent', content: `Error: ${data.error}` }]);
            setPhase('idle');
          }
        })
        .catch(err => {
          setMessages(prev => [...prev, { role: 'agent', content: `Network error: ${err.message}` }]);
          setPhase('idle');
        });
    } else {
      postIpc({ kind: "terminal-input-agent", data: trimmed });
    }
  }, [phase]);

  const handleSubmit = useCallback(() => {
    submitMessage(input);
  }, [input, submitMessage]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const isActive = phase === "pending" || phase === "streaming";
  const showEmpty = messages.length === 0 && !streaming;

  return (
    <div className="chat-layout">
      {/* Chat panel */}
      <div className="chat-panel">
        {/* Header */}
        <div className="chat-header">
          <div className="chat-header__left">
            <div className="chat-header__icon" style={{ color: (PROVIDER_ICONS[activeProvider] || DEFAULT_PROVIDER_ICON).color }}>
              {(PROVIDER_ICONS[activeProvider] || DEFAULT_PROVIDER_ICON).icon}
            </div>
            <span className="chat-header__title">Agent Chat</span>
            <span className="chat-header__project">{projectName}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ModelSelector
              providers={providers}
              activeProvider={activeProvider}
              models={ollamaModels}
              activeModel={activeModel}
              onSelectProvider={handleSelectProvider}
              onSelectModel={handleSelectModel}
            />
            {phase !== "idle" && (
              <div className={`chat-phase-badge ${phase}`}>
                <span className="chat-phase-badge__dot" />
                {phase === "pending" ? "Thinking" : phase === "streaming" ? "Writing" : phase}
              </div>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="chat-messages" ref={messagesRef}>
          {showEmpty && (
            <div className="chat-empty">
              <div className="chat-empty__icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                  <path d="M2 17l10 5 10-5"/>
                  <path d="M2 12l10 5 10-5"/>
                </svg>
              </div>
              <h3 className="chat-empty__title">Ready for {projectName}</h3>
              <p className="chat-empty__subtitle">Ask anything about the codebase, or try a suggestion below.</p>
              <div className="chat-empty__chips">
                {SUGGESTIONS.map((s) => (
                  <button key={s} className="chat-chip" onClick={() => submitMessage(s)}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`chat-row ${msg.role}`}>
              <div className="chat-row__avatar">
                {msg.role === "user" ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                ) : (
                  <span style={{ color: (PROVIDER_ICONS[activeProvider] || DEFAULT_PROVIDER_ICON).color }}>
                    {(PROVIDER_ICONS[activeProvider] || DEFAULT_PROVIDER_ICON).icon}
                  </span>
                )}
              </div>
              <div className="chat-row__body">
                <span className="chat-row__role">{msg.role === "user" ? "You" : "Agent"}</span>
                <div className={`chat-bubble ${msg.role}`}>{msg.content}</div>
              </div>
            </div>
          ))}

          {/* Streaming in progress */}
          {streaming && (
            <div className="chat-row agent">
              <div className="chat-row__avatar">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                  <path d="M2 17l10 5 10-5"/>
                  <path d="M2 12l10 5 10-5"/>
                </svg>
              </div>
              <div className="chat-row__body">
                <span className="chat-row__role">Agent</span>
                <div className="chat-bubble agent chat-bubble--streaming">
                  {streaming}
                  <span className="chat-cursor">|</span>
                </div>
              </div>
            </div>
          )}

          {/* Thinking indicator */}
          {phase === "pending" && !streaming && (
            <div className="chat-row agent">
              <div className="chat-row__avatar">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                  <path d="M2 17l10 5 10-5"/>
                  <path d="M2 12l10 5 10-5"/>
                </svg>
              </div>
              <div className="chat-row__body">
                <span className="chat-row__role">Agent</span>
                <div className="chat-bubble agent chat-bubble--thinking">
                  <ThinkingDots />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="chat-composer">
          <div className="chat-composer__inner">
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isActive ? "Agent is working..." : "Message the agent..."}
              disabled={isActive}
              className="chat-composer__input"
            />
            <button
              onClick={handleSubmit}
              disabled={isActive || !input.trim()}
              className="chat-composer__send"
              title="Send message"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"/>
                <polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </div>
          <span className="chat-composer__hint">Press Enter to send</span>
        </div>
      </div>

      {/* Terminal panel */}
      <div className="chat-terminal-panel">
        <div className="terminal-block">
          <div className="terminal-title-bar">
            <span className="dot dot--red" />
            <span className="dot dot--yellow" />
            <span className="dot dot--green" />
            <span className="title-text">opentool &mdash; zsh</span>
          </div>
          <div><span className="prompt">$ </span>cd {projectName}</div>
          <div className="output">Ready. Agent commands will appear here.</div>
          <div><span className="prompt">$ </span><span className="terminal-cursor">&#9608;</span></div>
        </div>
      </div>
    </div>
  );
}
